import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Fade, Backdrop, Chip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";

// ---- shared palette (matches the rest of the app) -----------------------
const palette = {
  primary: "#4B49AC",
  primaryDark: "#37357D",
  primarySoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  green: "#1D9A6C",
  greenSoft: "#E6F6EF",
  red: "#E14B4B",
  redSoft: "#FDECEC",
  amber: "#C98A18",
  amberSoft: "#FBF1DD",
  blue: "#3873D9",
  blueSoft: "#EAF1FC",
};

// ---- status -> visual treatment -----------------------------------------
const STATUS_MAP = {
  PRESENT: {
    label: "Present",
    color: palette.green,
    soft: palette.greenSoft,
    icon: CheckCircleRoundedIcon,
  },
  ABSENT: {
    label: "Absent",
    color: palette.red,
    soft: palette.redSoft,
    icon: CancelRoundedIcon,
  },
  CANCELLED: {
    label: "Cancelled",
    color: palette.amber,
    soft: palette.amberSoft,
    icon: EventBusyRoundedIcon,
  },
  HOLIDAY: {
    label: "Holiday",
    color: palette.blue,
    soft: palette.blueSoft,
    icon: CelebrationRoundedIcon,
  },
};
const DEFAULT_STATUS = {
  label: "Scheduled",
  color: palette.slate,
  soft: "#F1F2F4",
  icon: HelpOutlineRoundedIcon,
};

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const ALL_TYPES = "ALL";

const formatTime = (t) => {
  if (!t) return null;
  // handles "HH:MM:SS" / "HH:MM" strings without assuming a Date object
  const parts = t.split(":");
  if (parts.length < 2) return t;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${suffix}`;
};

export default function PatientModal({ open, onClose, selectedPatient }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);

  useEffect(() => {
    if (open && selectedPatient?.id) {
      getPatientSessions();
    }
  }, [open, selectedPatient]);

  // Reset the filter back to "All" each time the modal is opened fresh,
  // so a filter picked for one patient doesn't silently carry over to the next.
  useEffect(() => {
    if (open) setTypeFilter(ALL_TYPES);
  }, [open, selectedPatient?.id]);

  const getPatientSessions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/app/patient/${selectedPatient.id}/sessions`,
      );
      setSessions(data || []);
    } catch (error) {
      console.error("Error fetching patient sessions:", error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  // ---- distinct services present in this patient's sessions --------
  const sessionTypes = useMemo(() => {
    const types = new Set();
    sessions.forEach((item) => {
      if (item.service_name) types.add(item.service_name);
    });
    return Array.from(types).sort();
  }, [sessions]);

  // ---- sessions narrowed down by the selected service filter -------------
  const filteredSessions = useMemo(() => {
    if (typeFilter === ALL_TYPES) return sessions;
    return sessions.filter((item) => item.service_name === typeFilter);
  }, [sessions, typeFilter]);

  // ---- group filtered sessions by month, like a holiday calendar --------
  const groups = useMemo(() => {
    const map = new Map();

    filteredSessions.forEach((item) => {
      const d = item.session_date ? new Date(item.session_date) : null;
      const valid = d && !isNaN(d.getTime());
      const key = valid
        ? `${d.getFullYear()}-${d.getMonth()}`
        : "unscheduled";
      const label = valid
        ? d.toLocaleString("en-US", { month: "long", year: "numeric" })
        : "Unscheduled";

      if (!map.has(key)) {
        map.set(key, {
          key,
          label,
          sortKey: valid ? d.getFullYear() * 12 + d.getMonth() : Infinity,
          items: [],
        });
      }
      map.get(key).items.push({ ...item, __date: valid ? d : null });
    });

    const list = Array.from(map.values()).sort((a, b) => a.sortKey - b.sortKey);
    list.forEach((g) =>
      g.items.sort((a, b) => (a.__date && b.__date ? a.__date - b.__date : 0)),
    );
    return list;
  }, [filteredSessions]);

  const totalCount = sessions.length;
  const visibleCount = filteredSessions.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: { timeout: 300, sx: { backgroundColor: "rgba(17, 24, 39, 0.55)" } },
      }}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          maxHeight: "82vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "22px 28px",
          background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            backgroundColor: "rgba(255,255,255,0.16)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CalendarMonthRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: 0,
              color: "#fff",
              fontSize: "18px",
              fontWeight: 700,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedPatient?.patient_name
              ? `${selectedPatient.patient_name}'s Sessions`
              : "Patient Sessions"}
          </h3>
          <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.75)", fontSize: "12.5px" }}>
            {typeFilter === ALL_TYPES
              ? `${totalCount} session${totalCount === 1 ? "" : "s"} across ${groups.length} month${groups.length === 1 ? "" : "s"}`
              : `${visibleCount} of ${totalCount} session${totalCount === 1 ? "" : "s"} · ${typeFilter}`}
          </p>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.12)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Type filter chips */}
      {sessionTypes.length > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            padding: "14px 28px",
            backgroundColor: "#fff",
            borderBottom: `1px solid ${palette.line}`,
          }}
        >
          <FilterListRoundedIcon sx={{ fontSize: "16px", color: palette.slate, mr: "2px" }} />
          <Chip
            label="All"
            size="small"
            onClick={() => setTypeFilter(ALL_TYPES)}
            sx={{
              fontWeight: 700,
              fontSize: "12px",
              color: typeFilter === ALL_TYPES ? "#fff" : palette.ink,
              backgroundColor: typeFilter === ALL_TYPES ? palette.primary : palette.bg,
              border: `1px solid ${typeFilter === ALL_TYPES ? palette.primary : palette.line}`,
              "&:hover": {
                backgroundColor: typeFilter === ALL_TYPES ? palette.primaryDark : palette.primarySoft,
              },
            }}
          />
          {sessionTypes.map((type) => {
            const active = typeFilter === type;
            return (
              <Chip
                key={type}
                label={type}
                size="small"
                onClick={() => setTypeFilter(type)}
                sx={{
                  fontWeight: 700,
                  fontSize: "12px",
                  color: active ? "#fff" : palette.ink,
                  backgroundColor: active ? palette.primary : palette.bg,
                  border: `1px solid ${active ? palette.primary : palette.line}`,
                  "&:hover": {
                    backgroundColor: active ? palette.primaryDark : palette.primarySoft,
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      <DialogContent
        sx={{
          padding: 0,
          backgroundColor: palette.bg,
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box sx={{ padding: "40px", textAlign: "center", color: palette.slate, fontSize: "14px" }}>
            Loading sessions…
          </Box>
        ) : totalCount === 0 ? (
          <Box sx={{ padding: "48px 28px", textAlign: "center" }}>
            <EventNoteRoundedIcon sx={{ fontSize: 40, color: palette.line }} />
            <p style={{ margin: "10px 0 0", color: palette.slate, fontSize: "14px" }}>
              No sessions have been recorded for this patient yet.
            </p>
          </Box>
        ) : visibleCount === 0 ? (
          <Box sx={{ padding: "48px 28px", textAlign: "center" }}>
            <FilterListRoundedIcon sx={{ fontSize: 40, color: palette.line }} />
            <p style={{ margin: "10px 0 0", color: palette.slate, fontSize: "14px" }}>
              No "{typeFilter}" sessions for this patient.
            </p>
          </Box>
        ) : (
          <Box sx={{ padding: "8px 0 20px" }}>
            {groups.map((group) => (
              <Box key={group.key} sx={{ mb: "6px" }}>
                {/* Month header — sticky, holiday-page style */}
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 28px",
                    backgroundColor: palette.bg,
                    borderBottom: `1px solid ${palette.line}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: palette.primaryDark,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {group.label}
                  </span>
                  <Box
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: palette.primary,
                      backgroundColor: palette.primarySoft,
                      borderRadius: "999px",
                      padding: "1px 9px",
                    }}
                  >
                    {group.items.length}
                  </Box>
                </Box>

                {/* Sessions within the month */}
                <Box sx={{ padding: "6px 28px 4px" }}>
                  {group.items.map((item, idx) => {
                    const status = STATUS_MAP[item.status] || DEFAULT_STATUS;
                    const StatusIcon = status.icon;
                    const day = item.__date ? item.__date.getDate() : "–";
                    const monthAbbr = item.__date ? MONTH_ABBR[item.__date.getMonth()] : "";
                    const startTime = formatTime(item.start_time);
                    const endTime = formatTime(item.end_time);

                    return (
                      <Box
                        key={item.session_id ?? idx}
                        sx={{
                          display: "flex",
                          gap: "16px",
                          alignItems: "flex-start",
                          padding: "14px 4px",
                          borderBottom:
                            idx !== group.items.length - 1 ? `1px solid ${palette.line}` : "none",
                        }}
                      >
                        {/* Date badge */}
                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 52,
                            textAlign: "center",
                            borderRadius: "10px",
                            border: `1px solid ${status.soft}`,
                            backgroundColor: status.soft,
                            padding: "6px 0",
                          }}
                        >
                          <div style={{ fontSize: "16px", fontWeight: 700, color: status.color, lineHeight: 1 }}>
                            {day}
                          </div>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              color: status.color,
                              textTransform: "uppercase",
                              marginTop: "2px",
                            }}
                          >
                            {monthAbbr}
                          </div>
                        </Box>

                        {/* Details */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: "8px",
                              mb: "4px",
                            }}
                          >
                            <MedicalServicesRoundedIcon sx={{ fontSize: "15px", color: palette.slate }} />
                            <span style={{ fontSize: "14px", fontWeight: 700, color: palette.ink }}>
                              {item.service_name || "Session"}
                            </span>
                            {item.session_number != null && (
                              <span style={{ fontSize: "12px", color: palette.slate }}>
                                · Session {item.session_number}
                              </span>
                            )}
                            {item.session_type && (
                              <Box
                                sx={{
                                  fontSize: "10.5px",
                                  fontWeight: 700,
                                  color: palette.slate,
                                  border: `1px solid ${palette.line}`,
                                  borderRadius: "999px",
                                  padding: "1px 8px",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {item.session_type}
                              </Box>
                            )}
                          </Box>

                          {(startTime || endTime) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: palette.slate,
                                fontSize: "12.5px",
                                mb: item.notes ? "4px" : 0,
                              }}
                            >
                              <AccessTimeRoundedIcon sx={{ fontSize: "14px" }} />
                              {startTime}
                              {endTime ? ` – ${endTime}` : ""}
                            </Box>
                          )}

                          {item.notes && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "6px",
                                color: palette.slate,
                                fontSize: "12.5px",
                              }}
                            >
                              <StickyNote2RoundedIcon sx={{ fontSize: "14px", mt: "1px" }} />
                              <span>{item.notes}</span>
                            </Box>
                          )}
                        </Box>

                        {/* Status pill */}
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "4px 10px",
                            borderRadius: "999px",
                            backgroundColor: status.soft,
                            color: status.color,
                            fontSize: "11.5px",
                            fontWeight: 700,
                          }}
                        >
                          <StatusIcon sx={{ fontSize: "13px" }} />
                          {status.label}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
